import { z } from 'zod';

import type { SocialConnection } from '@nucleus-commerce/api-client/admin';

/** What the Connection step shows; `disconnected` covers "never connected" and `revoked`. */
export type ConnectionState = 'disconnected' | 'connected' | 'expired' | 'error';

export function connectionState(connection: SocialConnection | undefined): ConnectionState {
  if (!connection || connection.status === 'revoked') return 'disconnected';
  return connection.status;
}

/** The store's row for one network; `provider` is a string so a one-value enum compares cleanly. */
export function findConnection(
  items: SocialConnection[],
  provider: string,
): SocialConnection | undefined {
  return items.find((row) => row.provider === provider);
}

/** How the account reads on screen: `@username`, or the network's id when it has none. */
export function accountHandle(
  connection: Pick<SocialConnection, 'username' | 'externalAccountId'>,
) {
  return connection.username ? `@${connection.username}` : connection.externalAccountId;
}

export const ADS_SCOPES = ['ads:read', 'ads:write'] as const;

/** The ads step needs both scopes; a connection made before the ads consent carries neither. */
export function hasAdsScopes(connection: SocialConnection | undefined): boolean {
  if (!connection) return false;
  return ADS_SCOPES.every((scope) => connection.scopes.includes(scope));
}

/** `?error=` values the callback can land with; anything else is ignored. */
export const CALLBACK_ERRORS = [
  'cancelled',
  'business_required',
  'provider_rejected',
  'rate_limited',
] as const;
export type CallbackError = (typeof CALLBACK_ERRORS)[number];

/**
 * Search params the API's OAuth callback redirects to. Unknown values fall back to nothing so a
 * hand-edited URL never breaks the page.
 */
export const channelsSearchSchema = z.object({
  connected: z.enum(['pinterest']).optional().catch(undefined),
  error: z.enum(CALLBACK_ERRORS).optional().catch(undefined),
});
export type ChannelsSearch = z.infer<typeof channelsSearchSchema>;

/** Where Pinterest explains converting a personal account; shown with `business_required`. */
export const PINTEREST_BUSINESS_ACCOUNT_URL =
  'https://help.pinterest.com/en/business/article/get-a-business-account';
